<template>
  <teleport to="body">
    <div class="fixed inset-0 z-[9999] flex items-center justify-center" @click.self="$emit('close')">
      <div class="fixed inset-0 bg-black/40" />
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 flex flex-col overflow-hidden">
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 class="text-sm font-semibold text-neutral-900">Add to phone book</h2>
          <button class="text-gray-400 hover:text-gray-600" aria-label="Close" @click="$emit('close')">
            <FontAwesomeIcon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <div class="p-4 space-y-3">
          <p v-if="deactivatedEntry" class="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
            This number is already in the phone book as
            <span data-spoof-kind="practice" class="sensitive font-medium">{{ deactivatedEntry.name }}</span>,
            but it has been deactivated. Ask an admin to reactivate it.
          </p>
          <p v-if="formError" class="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">{{ formError }}</p>
          <div>
            <label for="add-contact-name" class="block text-xs text-gray-600 mb-1">Name</label>
            <input
              id="add-contact-name"
              v-model="form.name"
              type="text"
              placeholder="Lakeside DME"
              data-spoof-kind="practice" class="sensitive w-full border rounded px-2 py-1.5 text-sm"
              :class="fieldErrors.name ? 'border-red-400' : 'border-gray-300'"
            />
            <p v-for="message in fieldErrors.name" :key="message" class="mt-1 text-xs text-red-600">{{ message }}</p>
          </div>
          <div>
            <label for="add-contact-phone" class="block text-xs text-gray-600 mb-1">Phone number</label>
            <input
              id="add-contact-phone"
              v-model="form.phone_number"
              v-maska="{ mask: '(###) ###-####' }"
              type="tel"
              placeholder="(555) 123-4567"
              data-spoof-kind="phone" class="sensitive w-full border rounded px-2 py-1.5 text-sm"
              :class="fieldErrors.phone_number ? 'border-red-400' : 'border-gray-300'"
            />
            <p v-for="message in fieldErrors.phone_number" :key="message" class="mt-1 text-xs text-red-600">{{ message }}</p>
          </div>
          <div>
            <label for="add-contact-role" class="block text-xs text-gray-600 mb-1">Role</label>
            <select
              id="add-contact-role"
              v-model="form.role_id"
              class="w-full border rounded px-2 py-1.5 text-sm bg-white"
              :class="fieldErrors.role_id ? 'border-red-400' : 'border-gray-300'"
            >
              <option :value="null" disabled>Select role...</option>
              <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.display_name }}</option>
            </select>
            <p v-for="message in fieldErrors.role_id" :key="message" class="mt-1 text-xs text-red-600">{{ message }}</p>
          </div>
          <div>
            <label for="add-contact-notes" class="block text-xs text-gray-600 mb-1">Notes <span class="text-gray-400">(optional)</span></label>
            <textarea
              id="add-contact-notes"
              v-model="form.notes"
              rows="3"
              placeholder="Hours, who to ask for, fax first..."
              class="sensitive w-full border rounded px-2 py-1.5 text-sm"
              :class="fieldErrors.notes ? 'border-red-400' : 'border-gray-300'"
              data-demo-blur
            />
            <p v-for="message in fieldErrors.notes" :key="message" class="mt-1 text-xs text-red-600">{{ message }}</p>
          </div>
        </div>

        <div class="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <button type="button" class="btn-secondary-sm" :disabled="saving" @click="$emit('close')">Cancel</button>
          <button type="button" class="btn-primary-sm" :disabled="!canSave" @click="save">
            {{ saving ? 'Adding...' : 'Add contact' }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
// @ts-check
import { vMaska } from 'maska/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { createPhoneBookEntry } from '@/services/api/telehealthPhoneBook'
import { listParticipantRoles } from '@/services/api/voiceApi'
import { parseTelehealthError } from '@/services/api/telehealthErrors'

/** @typedef {import('@/services/api/telehealthPhoneBook').PhoneBookEntry} PhoneBookEntry */

export default {
  name: 'AddContactModal',
  components: { FontAwesomeIcon },
  directives: { maska: vMaska },
  emits: ['close', 'added'],
  data() {
    return {
      form: { name: '', phone_number: '', role_id: null, notes: '' },
      /** @type {import('@/services/api/voiceApi').TelehealthParticipantRole[]} */
      roles: [],
      /** @type {Record<string, string[]>} */
      fieldErrors: {},
      /** @type {string | null} */
      formError: null,
      /** @type {PhoneBookEntry | null} */
      deactivatedEntry: null,
      saving: false,
    }
  },
  computed: {
    canSave() {
      return !this.saving && !!this.form.name.trim() && this.form.phone_number.replace(/\D/g, '').length === 10 && !!this.form.role_id
    },
  },
  async mounted() {
    try {
      this.roles = await listParticipantRoles({ is_system: false })
    } catch (err) {
      this.formError = parseTelehealthError(err, 'Failed to load roles').summary
    }
  },
  methods: {
    async save() {
      if (!this.canSave) return
      this.saving = true
      this.fieldErrors = {}
      this.formError = null
      this.deactivatedEntry = null
      try {
        const entry = await createPhoneBookEntry({
          name: this.form.name.trim(),
          phone_number: `+1${this.form.phone_number.replace(/\D/g, '')}`,
          role_id: /** @type {number} */ (this.form.role_id),
          notes: this.form.notes.trim(),
        })
        this.$emit('added', entry, { existing: false })
      } catch (err) {
        this.handleError(err)
      } finally {
        this.saving = false
      }
    },
    /** @param {any} err */
    handleError(err) {
      /** @type {PhoneBookEntry | undefined} */
      const existing = err?.response?.status === 409 ? err.response.data?.entry : undefined
      if (existing?.is_active) {
        this.$emit('added', existing, { existing: true })
        return
      }
      if (existing) {
        this.deactivatedEntry = existing
        return
      }
      const { message, fieldErrors } = parseTelehealthError(err, 'Failed to add contact')
      this.fieldErrors = fieldErrors
      this.formError = message || (Object.keys(fieldErrors).length ? null : 'Failed to add contact')
    },
  },
}
</script>
